// Client-side state for the standard collection-endpoint params (page /
// per_page / q / sort / order). One hook drives search + pagination on any
// portal list page: search input changes are debounced into `q`, and any
// filter change snaps back to page 1 so the result window stays valid.
import { useEffect, useMemo, useState } from "react";
import { DEFAULT_PER_PAGE, PER_PAGE_OPTIONS, pageCount } from "@pgs-core/config";

export { DEFAULT_PER_PAGE, PER_PAGE_OPTIONS, pageCount };
const SEARCH_DEBOUNCE_MS = 300;

export type ListOrder = "asc" | "desc";

/** Pagination meta echoed by paginated collection responses. */
export interface ListMeta {
  total: number;
  page?: number;
  per_page?: number;
}

export interface ListControls {
  /** Live search-input value (render this in the input). */
  search: string;
  /** Debounced search value — what the current query string uses. */
  q: string;
  page: number;
  perPage: number;
  sort: string | null;
  order: ListOrder | null;
  /** `page=…&per_page=…[&q=…][&sort=…&order=…]` for the API call. */
  queryString: string;
  setSearch: (search: string) => void;
  setPage: (page: number) => void;
  setPerPage: (perPage: number) => void;
  setSort: (sort: string | null, order?: ListOrder) => void;
}

export interface ListControlsInit {
  perPage?: number;
  sort?: string;
  order?: ListOrder;
}

export function useListControls(init: ListControlsInit = {}): ListControls {
  const [search, setSearchValue] = useState("");
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [perPage, setPerPageValue] = useState(init.perPage ?? DEFAULT_PER_PAGE);
  const [sort, setSortValue] = useState<string | null>(init.sort ?? null);
  const [order, setOrderValue] = useState<ListOrder | null>(init.order ?? null);

  // Debounce keystrokes into `q`; an emptied input applies immediately.
  useEffect(() => {
    if (search === q) return;
    if (!search) {
      setQ("");
      setPage(1);
      return;
    }
    const timer = setTimeout(() => {
      setQ(search);
      setPage(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- q is only written here
  }, [search]);

  const queryString = useMemo(() => {
    const params = new URLSearchParams({ page: String(page), per_page: String(perPage) });
    if (q) params.set("q", q);
    if (sort) {
      params.set("sort", sort);
      if (order) params.set("order", order);
    }
    return params.toString();
  }, [page, perPage, q, sort, order]);

  return {
    search,
    q,
    page,
    perPage,
    sort,
    order,
    queryString,
    setSearch: setSearchValue,
    setPage,
    setPerPage: (next) => {
      setPerPageValue(next);
      setPage(1);
    },
    setSort: (nextSort, nextOrder) => {
      setSortValue(nextSort);
      setOrderValue(nextOrder ?? null);
      setPage(1);
    }
  };
}
